/**
 * Fatiado de lib/api.ts por domínio (#131, regra R3).
 */
import { get, post, postForm } from './client.js'
// ─── Menu Import — importação de cardápio por OCR (chat) ─────────────────────

export type ImportQuickReply = {
  label: string
  /** B2 (UX 5641191454): pergunta de idioma manda {label: nome nativo, value: código} */
  value?: string
  action: 'reply' | 'all_ok' | 'dismiss'
}

export type ImportMessage = {
  id: string
  role: 'user' | 'assistant'
  kind: string
  content: string
  /** strings (chat OCR) ou objetos com ação (revisão) */
  quick_replies?: Array<string | ImportQuickReply>
  photo_page?: number | null
  created_at: number
}

export type ImportDraftFlag = {
  id: string
  category: string
  product: string
  reason: string
  question: string
  kind: 'extra_removed' | 'low_confidence'
  extra?: { name: string; price_cents: number; kind?: string }
}

export type ImportDraftAllergen = {
  code: number
  source: 'menu' | 'ai'
  confidence?: number
}

export type ImportDraftProduct = {
  name: string
  description?: string
  price_cents: number
  /** #299: preço não reconhecido («PSM», «según mercado») — mostra o selo */
  price_status?: 'ok' | 'zero' | 'market'
  /** #299: preço impresso no Menu quando não é número */
  price_raw?: string
  variants: { name: string; price_cents: number; price_status?: 'ok' | 'zero' | 'market' }[]
  extras: { name: string; price_cents: number; kind?: string }[]
  ingredients: string[]
  /** #325: alergénios sugeridos (impressos ou inferidos pela IA) */
  allergens?: ImportDraftAllergen[]
  /** #325: ingredientes prováveis que a carta não imprime */
  ingredient_suggestions?: { name: string; confidence?: number }[]
  /** #325: decisões do dono na revisão (enviadas no commit) */
  allergens_confirmed?: number[]
  ingredients_accepted?: string[]
  ingredients_rejected?: string[]
  /**
   * #320: tradução do item impressa na 2.ª língua do cardápio bilíngue —
   * sai da descrição e vira linha de tradução no commit
   */
  translations?: { locale: string; name?: string; description?: string }[]
  /** #321: calorias impressas no cardápio (kcal), editáveis na revisão */
  calories_kcal?: number
  has_photo: boolean
  photo_r2_key?: string
  review?: 'pending' | 'ok' | 'fix'
  review_comment?: string
  fixed?: boolean
  /** tipo p/ compatibilidade de adicionais (OCR parte A) */
  type?: string
  /** confiança da leitura OCR 0–1 */
  confidence?: number
  /** id estável atribuído pelo client — chaves de revisão não dependem do nome */
  rid?: string
}

export type ImportDraft = {
  locale: string
  currency?: string
  restaurant: { name?: string; about?: string; logo_r2_key?: string }
  categories: {
    name: string
    products: ImportDraftProduct[]
    translations?: { locale: string; title: string }[]
  }[]
  /**
   * #321: taxa de serviço/coperto lida no cardápio — SUGESTÃO confirmável
   * pelo dono (nunca cobrada automaticamente; decisão de produto)
   */
  service_charges?: {
    label?: string
    percent?: number
    amount_cents?: number
    per: 'person' | 'bill'
  }[]
  /** itens que precisam de atenção (regra dura + passagem 2 do OCR) */
  flags?: ImportDraftFlag[]
}

export type MenuImportView = {
  id: string
  status: 'processing' | 'reviewing' | 'extracted' | 'committed' | 'error' | 'cancelled'
  awaiting: 'input' | 'photo' | 'none'
  /** estágio ao vivo do pipeline: ocr | agent | semantic | review | done */
  current_step?: string | null
  locale: string | null
  needs_locale_confirmation: boolean
  /** #299: moeda detectada ou, sem detecção, a padrão do restaurante */
  currency: string | null
  /** #299: false = moeda veio do padrão do restaurante (país/locale) */
  currency_detected?: boolean
  /** #299 crit. 7: moeda lida no Menu ou provável pelo idioma/região */
  currency_suggested?: string | null
  /** #299 crit. 7: moeda padrão do restaurante diverge da provável do Menu */
  needs_currency_confirmation?: boolean
  /** #299 crit. 7: confirmação da moeda já gravada pelo servidor */
  currency_confirmed?: boolean
  /** #299: moeda padrão do restaurante (país > locale) — base do aviso */
  restaurant_currency?: string | null
  /** #486 (item 4): país do restaurante — compõe o locale do Intl (formato dos valores) */
  restaurant_country?: string | null
  /** #299: idioma principal do restaurante (comparação com o do cardápio) */
  restaurant_locale?: string | null
  /** #299 P6: idioma confirmado pelo dono (chat ou revisão) — revisão vem feita */
  locale_confirmed?: boolean
  chat_locale: string
  page_count: number
  processed_pages: number
  restaurant: { name: string | null; about: string | null; logo_url: string | null }
  draft: ImportDraft | null
  error: string | null
  messages: ImportMessage[]
  created_at: number
  /** status por página p/ atividade ao vivo durante o OCR */
  pages?: { n: number; status: 'pending' | 'ocr_done' | 'error' }[]
  updated_at?: number
  /**
   * #489 (item 5): nº de pratos pausados por falta de preço, autoritativo do
   * commit (gravado em commit_counts) — null antes do commit ou em commits
   * anteriores ao #489. A tela de conclusão mostra a linha com n > 0.
   */
  paused_dishes?: number | null
  /** #489: nº de opções pausadas a 0 em pratos que ficaram no ar */
  paused_options?: number | null
}

export type ReviewSubmission = {
  items: {
    category: string
    name: string
    action: 'ok' | 'fix' | 'remove'
    comment?: string
    /** nome no rascunho ANTES do rename na revisão — chave de match no servidor */
    original_name?: string
  }[]
  group_comments?: { category: string; comment: string }[]
}

export type ReviewOutcome =
  | { status: 'pending'; pending: number }
  | { status: 'reviewing'; fixing: number; removed: number }
  | { status: 'applied'; applied: number; removed: number; total: number }

/**
 * #489 (item 5): resposta do commit — as contagens de pausados são
 * autoritativas do servidor (mesma regra do plano); ausentes em re-commits
 * de imports anteriores ao #489.
 */
export type CommitResult = {
  categories: number
  products: number
  paused_dishes?: number
  paused_options?: number
}

export const importsApi = {
  submitReview: (id: string, body: ReviewSubmission) =>
    post<ReviewOutcome>(`/admin/menu-imports/${id}/review`, body),
  markAllReviewed: (id: string) =>
    post<ReviewOutcome>(`/admin/menu-imports/${id}/review/all-ok`, {}),
  /** Cardápio digital por link — leitura no servidor, mesma revisão. */
  fromUrl: (url: string, chatLocale: string) =>
    post<{ id: string }>('/admin/menu-imports/from-url', { url, chat_locale: chatLocale }),
  upload: (pages: Blob[], chatLocale: string) => {
    const form = new FormData()
    form.append('chat_locale', chatLocale)
    pages.forEach((p, i) => form.append('pages', p, `page-${i + 1}.jpg`))
    return postForm<{ id: string }>('/admin/menu-imports', form)
  },
  get: (id: string) => get<MenuImportView>(`/admin/menu-imports/${id}`),
  /**
   * #299 crit. 7 (DECISÃO B): correção da leitura — POST /:id/currency só
   * aceita a MOEDA DO RESTAURANTE e grava menu_imports.currency +
   * currency_confirmed_at. 422 CURRENCY_NOT_RESTAURANT = outra moeda
   * qualquer (não há conversão de preços).
   */
  confirmCurrency: (id: string, currency: string) =>
    post<{ currency: string; currency_confirmed_at: number }>(
      `/admin/menu-imports/${id}/currency`,
      { currency },
    ),
  /** Decide um aviso em 1 clique: keep = quero manter · accept = quero mudar. */
  resolveFlag: (id: string, flagId: string, action: 'keep' | 'accept') =>
    post<{ resolved: number }>(`/admin/menu-imports/${id}/flags/${flagId}`, { action }),
  /** Mesma decisão aplicada a todos os avisos de uma vez. */
  resolveAllFlags: (id: string, action: 'keep' | 'accept') =>
    post<{ resolved: number }>(`/admin/menu-imports/${id}/flags/resolve-all`, { action }),
  /** Retomada forçada da corrente (processo travado em background). */
  kick: (id: string) => post<{ reset_pages: number }>(`/admin/menu-imports/${id}/kick`, {}),
  /** skip=true: "Seguir sem responder" — a pergunta não volta (#305) */
  message: (id: string, text: string, skip = false) =>
    post<null>(`/admin/menu-imports/${id}/message`, skip ? { text, skip: true } : { text }),
  sendPage: (id: string, pageNumber: number, page: Blob) => {
    const form = new FormData()
    form.append('page', page, `page-${pageNumber}.jpg`)
    return postForm<null>(`/admin/menu-imports/${id}/page/${pageNumber}`, form)
  },
  commit: (
    id: string,
    body: {
      locale: string
      /** #299: idioma ≠ do restaurante confirmado pelo dono */
      confirm_locale?: boolean
      mode: 'append' | 'replace'
      apply_restaurant_info: boolean
      draft: ImportDraft
    },
  ) => post<CommitResult>(`/admin/menu-imports/${id}/commit`, body),
  cancel: (id: string) => post<null>(`/admin/menu-imports/${id}/cancel`, {}),
}
